/**
 * Перевод серверных ошибок на русский.
 *
 * Бэкенд отдаёт сообщения на английском (`{"error": "Product is not
 * available in current store inventory"}`), и страницы показывали их
 * пользователю как есть. Локализовать ответы API — отдельная задача на
 * бэкенде; здесь словарь на стороне клиента, чтобы интерфейс не был
 * наполовину английским.
 *
 * Незнакомое сообщение возвращается без изменений: показать английский
 * текст лучше, чем проглотить ошибку и оставить пользователя без причины.
 */

/** Точные совпадения. Ключ — сообщение бэкенда как есть. */
const EXACT = {
  "Product is not available in current store inventory":
    "Товара нет на складе этого магазина",
  "Product with this barcode not found": "Товар с таким штрихкодом не найден",
  "No active shift found": "Нет открытой смены",
  "Payment does not exist": "Платёж не найден",
  "Barcode parameter is required": "Укажите штрихкод",
  "Barcodes parameter is required": "Укажите штрихкоды",
  "Barcode and store parameter are required": "Укажите штрихкод и магазин",
  "Barcode partial parameter is required": "Укажите часть штрихкода",
  "Name partial parameter is required": "Укажите часть названия",
  "Address partial parameter is required": "Укажите часть адреса",
  "Barcode is required.": "Укажите штрихкод",
  "At least one product is required.": "Добавьте хотя бы один товар",
  "Barcodes must be a non-empty list.": "Список штрихкодов не может быть пустым",
  "Cannot confirm an empty wholesale sale.":
    "Нельзя подтвердить продажу без товаров",
  "Customer is inactive.": "Клиент неактивен",
  "Duplicate product_id found in inventories list":
    "Товар встречается в списке дважды",
  "Either shift_id or store_id must be provided": "Укажите смену или магазин",
  "Initial payment cannot be negative.":
    "Первоначальный платёж не может быть отрицательным",
  "Inventories list cannot be empty": "Список остатков не может быть пустым",
  "Items can only be added to a draft sale.":
    "Товары можно добавлять только в черновик продажи",
  "Minimum amount cannot be greater than maximum amount":
    "Минимальная сумма больше максимальной",
  "Minimum price cannot be greater than maximum price":
    "Минимальная цена больше максимальной",
  "Minimum quantity cannot be greater than maximum quantity":
    "Минимальное количество больше максимального",
  "Cannot filter for both in stock and out of stock simultaneously":
    "Нельзя одновременно фильтровать «в наличии» и «нет в наличии»",
  "One of barcodes is already used as a primary product barcode.":
    "Один из штрихкодов уже занят основным штрихкодом товара",
  "One of barcodes is already used as an additional product barcode.":
    "Один из штрихкодов уже занят дополнительным штрихкодом другого товара",
  "This barcode is already used as an additional barcode.":
    "Этот штрихкод уже занят дополнительным штрихкодом другого товара",
  "Primary barcode must be included in barcodes list.":
    "Основной штрихкод должен быть в списке штрихкодов",
  "This field may not be blank.": "Поле не может быть пустым",
  "This field may not be null.": "Поле не может быть пустым",
  "This field is required.": "Обязательное поле",
  "A valid number is required.": "Введите число",
  "Product with this barcode already exists.":
    "Товар с таким штрихкодом уже существует",
};

/**
 * Сообщения с переменной частью (id товара, название). Проверяются
 * после точных совпадений.
 */
const PREFIX = [
  [
    "Product is not available in current store inventory:",
    "Товара нет на складе этого магазина",
  ],
];

/** Переводит одно сообщение. Незнакомое возвращает как есть. */
export function translateApiError(message) {
  if (typeof message !== "string") return message;
  const text = message.trim();
  if (EXACT[text]) return EXACT[text];
  for (const [prefix, ru] of PREFIX) {
    if (text.startsWith(prefix)) return ru;
  }
  return message;
}

/**
 * Достаёт сообщение из ошибки apiFetch и переводит его.
 *
 * Форма ответа у бэкенда непостоянная: где-то `error`, где-то `detail`,
 * где-то `message`, а DRF при валидации возвращает массив строк.
 *
 * @param {unknown} err — пойманная ошибка
 * @param {string} fallback — текст, если извлечь ничего не удалось
 */
export function apiErrorMessage(err, fallback = "Что-то пошло не так") {
  const data = err && err.data;

  if (typeof data === "string" && data.trim()) {
    return translateApiError(data);
  }

  if (data && typeof data === "object") {
    const direct = data.error || data.detail || data.message;
    if (typeof direct === "string" && direct.trim()) {
      return translateApiError(direct);
    }
    if (Array.isArray(direct) && direct.length) {
      return translateApiError(String(direct[0]));
    }
    // DRF по полям: { barcode: ["Обязательное поле."] }
    for (const value of Object.values(data)) {
      if (typeof value === "string" && value.trim()) {
        return translateApiError(value);
      }
      if (Array.isArray(value) && value.length && typeof value[0] === "string") {
        return translateApiError(value[0]);
      }
    }
  }

  if (err && typeof err.message === "string" && err.message.trim()) {
    return translateApiError(err.message);
  }

  return fallback;
}

function firstText(value) {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    const parts = value.map(firstText).filter(Boolean);
    return parts.join(" ");
  }
  if (value && typeof value === "object") {
    return Object.values(value).map(firstText).filter(Boolean).join(" ");
  }
  return "";
}

/**
 * Собирает ошибку валидации DRF в понятный текст с названием поля.
 *
 * DRF при 400 отдаёт `{ barcode: ["..."], wholesale_price: ["..."] }` без
 * `detail`. Если брать только `detail`/`message`, redux-toolkit подставляет
 * «Rejected», и пользователь не знает, что и где править.
 *
 * @param {unknown} data — тело ответа (`err.data`)
 * @param {Record<string, string>} labels — русские названия полей по имени в API
 * @param {string} fallback — текст, если в теле нет ничего пригодного
 * @returns {string} «Штрихкод: уже занят; Опт. цена: Введите число»
 */
export function apiFieldErrorsMessage(data, labels = {}, fallback = "") {
  if (typeof data === "string" && data.trim()) return translateApiError(data);
  if (!data || typeof data !== "object" || Array.isArray(data)) return fallback;

  const general = data.detail || data.message || data.error;
  const generalText = firstText(general);
  if (generalText) return translateApiError(generalText);

  const lines = [];
  for (const [field, value] of Object.entries(data)) {
    const text = translateApiError(firstText(value));
    if (!text) continue;
    if (field === "non_field_errors") {
      lines.push(text);
      continue;
    }
    lines.push(`${labels[field] || field}: ${text}`);
  }
  return lines.length ? lines.join("; ") : fallback;
}
